/**
 * Partner-authenticated Hub client for the developer portal.
 *
 * Auth model:
 *   • Partner enters client_id + client_secret on the login page.
 *   • We call POST /oauth/token to get an access_token (client_credentials grant).
 *   • Access token + scope + expiry live in sessionStorage — wiped on tab close.
 *   • Credentials themselves are NEVER stored — only the short-lived token.
 *
 * This is the ops-style "paste your credentials" pattern used by Stripe
 * Dashboard, AWS Console, etc. when browser cookies aren't appropriate.
 */

import { create } from "zustand";

const HUB_API_URL =
  (import.meta.env.VITE_HUB_API_URL as string | undefined) ??
  "https://hub.sumo.com";

interface SessionState {
  token: string | null;
  scope: string | null;
  expiresAt: number;
  clientId: string | null;
  loaded: boolean;
  login: (clientId: string, clientSecret: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  bootstrap: () => void;
}

const SS_KEY = "hub_developer_session";

interface PersistedSession {
  token: string;
  scope: string;
  expiresAt: number;
  clientId: string;
}

function loadPersisted(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.token || parsed.expiresAt < Date.now()) {
      sessionStorage.removeItem(SS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export const useSession = create<SessionState>((set) => ({
  token: null,
  scope: null,
  expiresAt: 0,
  clientId: null,
  loaded: false,

  bootstrap() {
    const persisted = loadPersisted();
    if (persisted) {
      set({
        token: persisted.token,
        scope: persisted.scope,
        expiresAt: persisted.expiresAt,
        clientId: persisted.clientId,
        loaded: true,
      });
    } else {
      set({ loaded: true });
    }
  },

  async login(clientId: string, clientSecret: string) {
    const body = new URLSearchParams({
      grant_type: "client_credentials",
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
      scope:
        "catalog:write captures:read refunds:create wallets:read assets:upload jobs:read",
    });
    let res: Response;
    try {
      res = await fetch(`${HUB_API_URL}/oauth/token`, {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch (e) {
      return {
        ok: false,
        error: `network error: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      return {
        ok: false,
        error: `${res.status}: ${text.slice(0, 200)}`,
      };
    }
    const data = (await res.json()) as {
      access_token: string;
      expires_in: number;
      scope: string;
    };
    const session: PersistedSession = {
      token: data.access_token,
      scope: data.scope,
      expiresAt: Date.now() + (data.expires_in - 60) * 1000,
      clientId: clientId.trim(),
    };
    sessionStorage.setItem(SS_KEY, JSON.stringify(session));
    set({
      token: session.token,
      scope: session.scope,
      expiresAt: session.expiresAt,
      clientId: session.clientId,
      loaded: true,
    });
    return { ok: true };
  },

  logout() {
    sessionStorage.removeItem(SS_KEY);
    set({ token: null, scope: null, expiresAt: 0, clientId: null });
  },
}));

export async function partnerFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const { token } = useSession.getState();
  if (!token) {
    throw new Error("not authenticated");
  }
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("content-type") && init.body) {
    headers.set("content-type", "application/json");
  }
  return fetch(`${HUB_API_URL}${path}`, { ...init, headers });
}
